import { Controller } from '@hotwired/stimulus';

// Visionneuse (LightboxComponent, sur <body>) : un lien
// data-action="lightbox#open" (ImageHelper#lightbox_link) ouvre son image
// dans la fenêtre, avec sa légende, son adresse de téléchargement et sa
// galerie (liens visibles du même groupe, dans l'ordre de la page :
// précédent / suivant par boutons, flèches du clavier ou glissement au
// doigt, compteur « 2 / 5 »). Image ajustée à l'écran ; un clic (double
// appui au doigt) passe en taille réelle, défilement pour s'y déplacer, au
// point visé ; le pincement du navigateur reste possible. Échap, Fermer ou
// clic sur le fond ferment ; focus sur Fermer à l'ouverture, rendu au lien
// à la fermeture ; page figée pendant l'ouverture ; fermée quand la page
// est quittée (cache Turbo).
const SWIPE = 50;
const DOUBLE_TAP = 300;

export default class extends Controller {
    static targets = ['dialog', 'caption', 'counter', 'download', 'closeButton', 'stage', 'image', 'loading', 'error', 'previous', 'next'];

    connect() {
        this.closeNow = () => this.close();
        document.addEventListener('turbo:before-cache', this.closeNow);
        window.addEventListener('pagehide', this.closeNow);
        if (!this.hasStageTarget) return;
        this.touchStart = (event) => this.startTouch(event);
        this.touchEnd = (event) => this.endTouch(event);
        this.stageTarget.addEventListener('touchstart', this.touchStart, { passive: true });
        this.stageTarget.addEventListener('touchend', this.touchEnd, { passive: true });
    }

    disconnect() {
        document.removeEventListener('turbo:before-cache', this.closeNow);
        window.removeEventListener('pagehide', this.closeNow);
        this.stageTarget?.removeEventListener('touchstart', this.touchStart);
        this.stageTarget?.removeEventListener('touchend', this.touchEnd);
        this.unlockPage();
    }

    open(event) {
        if (!this.hasDialogTarget || event.ctrlKey || event.metaKey || event.shiftKey || event.button > 0) return;
        event.preventDefault();
        const link = event.currentTarget;
        const group = link.dataset.lightboxGroupParam;
        const links = group ? [...document.querySelectorAll('a[data-lightbox-group-param]')]
            .filter((item) => item.dataset.lightboxGroupParam === group && item.checkVisibility()) : [link];
        this.items = (links.includes(link) ? links : [link]).map((item) => ({
            link: item,
            src: item.href,
            caption: item.dataset.lightboxCaptionParam || '',
            download: item.dataset.lightboxDownloadParam || '',
        }));
        this.trigger = link;
        this.show(this.items.findIndex((item) => item.link === link));
        if (!this.dialogTarget.open) this.dialogTarget.showModal();
        this.lockPage();
        this.closeButtonTarget.focus();
    }

    close() {
        if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close();
    }

    // Fermeture (Fermer, Échap, fond, page quittée) : page libérée, focus
    // rendu au lien.
    closed() {
        this.unlockPage();
        this.imageTarget.removeAttribute('src');
        if (this.trigger?.isConnected) this.trigger.focus();
        this.trigger = null;
    }

    previous() {
        if (this.items?.length > 1) this.show((this.index - 1 + this.items.length) % this.items.length);
    }

    next() {
        if (this.items?.length > 1) this.show((this.index + 1) % this.items.length);
    }

    key(event) {
        if (event.key === 'ArrowLeft') this.previous();
        else if (event.key === 'ArrowRight') this.next();
        else return;
        event.preventDefault();
    }

    // Clic sur le fond (hors image et boutons) : fermeture.
    backdrop(event) {
        if (event.target === this.stageTarget) this.close();
    }

    zoom(event) {
        if (Date.now() - (this.lastTouch || 0) < 600) return;
        this.toggleZoom(event.clientX, event.clientY);
    }

    loaded() {
        this.loadingTarget.hidden = true;
        this.imageTarget.classList.remove('opacity-0');
        this.updateZoomCursor();
    }

    failed() {
        if (!this.imageTarget.getAttribute('src')) return;
        this.loadingTarget.hidden = true;
        this.imageTarget.hidden = true;
        this.errorTarget.setAttribute('role', 'alert');
        this.errorTarget.hidden = false;
    }

    show(index) {
        this.index = Math.max(index, 0);
        const item = this.items[this.index];
        this.setZoomed(false);
        this.captionTarget.textContent = item.caption || 'Image';
        this.imageTarget.alt = item.caption;
        this.errorTarget.hidden = true;
        this.errorTarget.removeAttribute('role');
        this.imageTarget.hidden = false;
        this.imageTarget.classList.add('opacity-0');
        this.loadingTarget.hidden = false;
        this.imageTarget.src = item.src;
        if (this.imageTarget.complete && this.imageTarget.naturalWidth) this.loaded();
        this.downloadTarget.hidden = !item.download;
        if (item.download) this.downloadTarget.href = item.download;
        else this.downloadTarget.removeAttribute('href');
        const gallery = this.items.length > 1;
        this.counterTarget.hidden = !gallery;
        this.counterTarget.textContent = gallery ? `${this.index + 1} / ${this.items.length}` : '';
        this.previousTarget.hidden = !gallery;
        this.nextTarget.hidden = !gallery;
        if (gallery) this.preload();
    }

    preload() {
        [this.index - 1, this.index + 1].forEach((position) => {
            const item = this.items[(position + this.items.length) % this.items.length];
            new Image().src = item.src;
        });
    }

    // Taille réelle au point visé (x, y : coordonnées à l'écran), ou retour
    // à l'image ajustée. Sans effet sur une image plus petite que l'écran.
    toggleZoom(x, y) {
        const image = this.imageTarget;
        if (!this.zoomed && !this.zoomable) return;
        const before = image.getBoundingClientRect();
        const ratioX = (x - before.left) / before.width;
        const ratioY = (y - before.top) / before.height;
        this.setZoomed(!this.zoomed);
        if (!this.zoomed) return;
        const stage = this.stageTarget.getBoundingClientRect();
        this.stageTarget.scrollLeft = ratioX * image.naturalWidth - (x - stage.left);
        this.stageTarget.scrollTop = ratioY * image.naturalHeight - (y - stage.top);
    }

    setZoomed(zoomed) {
        this.zoomed = zoomed;
        this.imageTarget.classList.toggle('max-h-full', !zoomed);
        this.imageTarget.classList.toggle('max-w-full', !zoomed);
        this.imageTarget.classList.toggle('max-w-none', zoomed);
        this.dialogTarget.dataset.zoomed = zoomed;
        if (!zoomed) {
            this.stageTarget.scrollLeft = 0;
            this.stageTarget.scrollTop = 0;
        }
        this.updateZoomCursor();
    }

    get zoomable() {
        const image = this.imageTarget;
        return image.naturalWidth > image.clientWidth + 1 || image.naturalHeight > image.clientHeight + 1;
    }

    updateZoomCursor() {
        const image = this.imageTarget;
        image.classList.toggle('cursor-zoom-out', Boolean(this.zoomed));
        image.classList.toggle('cursor-zoom-in', !this.zoomed && this.zoomable);
    }

    startTouch(event) {
        if (event.touches.length !== 1) {
            this.touch = null;
            return;
        }
        const { clientX, clientY } = event.touches[0];
        this.touch = { x: clientX, y: clientY, time: Date.now() };
    }

    // Glissement horizontal : image précédente ou suivante (image ajustée,
    // page non agrandie par pincement) ; double appui sur l'image : zoom.
    endTouch(event) {
        const start = this.touch;
        this.touch = null;
        if (!start || event.changedTouches.length !== 1) return;
        this.lastTouch = Date.now();
        const { clientX, clientY } = event.changedTouches[0];
        const dx = clientX - start.x;
        const dy = clientY - start.y;
        const pinched = (window.visualViewport?.scale || 1) > 1.01;
        if (!this.zoomed && !pinched && Math.abs(dx) > SWIPE && Math.abs(dx) > 1.5 * Math.abs(dy)) {
            if (dx < 0) this.next();
            else this.previous();
            this.lastTap = 0;
            return;
        }
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10 || event.target !== this.imageTarget) return;
        if (this.lastTap && this.lastTouch - this.lastTap < DOUBLE_TAP) {
            this.lastTap = 0;
            this.toggleZoom(clientX, clientY);
        } else {
            this.lastTap = this.lastTouch;
        }
    }

    lockPage() {
        document.documentElement.classList.add('overflow-hidden');
    }

    unlockPage() {
        document.documentElement.classList.remove('overflow-hidden');
    }
}
